import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CURRENCY_CONFIG = {
  EUR: { symbol: '\u20ac', code: 'EUR', locale: 'de-DE', flag: '\ud83c\uddea\ud83c\uddfa' },
  USD: { symbol: '$', code: 'USD', locale: 'en-US', flag: '\ud83c\uddfa\ud83c\uddf8' },
  GBP: { symbol: '\u00a3', code: 'GBP', locale: 'en-GB', flag: '\ud83c\uddec\ud83c\udde7' },
  PLN: { symbol: 'z\u0142', code: 'PLN', locale: 'pl-PL', flag: '\ud83c\uddf5\ud83c\uddf1' }
};

const CurrencyContext = createContext();

export function CurrencyProvider({ children, language }) {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('preferred_currency');
    if (saved && CURRENCY_CONFIG[saved]) return saved;
    return 'EUR';
  });
  const [rates, setRates] = useState({ EUR: 1, USD: 1.08, GBP: 0.86, PLN: 4.28 });
  const [ratesLoaded, setRatesLoaded] = useState(false);
  const [geoDetected, setGeoDetected] = useState(false);

  // Fetch exchange rates
  useEffect(() => {
    axios.get(`${API}/exchange-rates`)
      .then(res => {
        if (res.data?.rates) {
          setRates(res.data.rates);
          setRatesLoaded(true);
        }
      })
      .catch(() => setRatesLoaded(true));
  }, []);

  // Auto-detect currency via geo-IP (only on first load, if user hasn't manually selected)
  useEffect(() => {
    const manuallySet = localStorage.getItem('currency_manual');
    if (manuallySet === 'true') return;

    axios.get(`${API}/geo-currency`)
      .then(res => {
        if (res.data?.currency && CURRENCY_CONFIG[res.data.currency]) {
          setCurrency(res.data.currency);
          setGeoDetected(true);
        }
      })
      .catch(() => {
        // Fallback: use language-based detection
        setGeoDetected(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setManualCurrency = useCallback((code) => {
    setCurrency(code);
    localStorage.setItem('preferred_currency', code);
    localStorage.setItem('currency_manual', 'true');
  }, []);

  const formatPrice = useCallback((eurAmount) => {
    const amount = parseFloat(eurAmount);
    if (isNaN(amount)) return `${CURRENCY_CONFIG[currency].symbol}0.00`;
    const converted = amount * (rates[currency] || 1);
    return `${CURRENCY_CONFIG[currency].symbol}${converted.toFixed(2)}`;
  }, [currency, rates]);

  const convertPrice = useCallback((eurAmount) => {
    const amount = parseFloat(eurAmount);
    if (isNaN(amount)) return 0;
    return (amount * (rates[currency] || 1)).toFixed(2);
  }, [currency, rates]);

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency: setManualCurrency,
      formatPrice,
      convertPrice,
      rates,
      ratesLoaded,
      geoDetected,
      currencyConfig: CURRENCY_CONFIG[currency],
      allCurrencies: CURRENCY_CONFIG
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
